/**
 * LayoutOrganizAI — Sidebar lateral
 * Identidade: Leve · Humano · Simples · Amigável
 * Estrutura: Sidebar 248px (fixo) + Área de conteúdo
 * Fontes: Plus Jakarta Sans
 * Cores: Teal #0D9488, fundo neutral-50
 */
import React, { useState, useEffect } from 'react';
import {
  LogOut, User, Power, ListChecks, Menu, X,
} from 'lucide-react';
import { podeAcessarAba } from '../utils/permissoes';
import SaldoDisparos from '../components/shared/SaldoDisparos';

// ─── Tokens ────────────────────────────────────────────────────────────────────
const C = {
  primary:    '#0D9488',
  primaryHov: '#0F766E',
  primaryBg:  '#F0FDFA',
  primaryMid: '#CCFBF1',
  primaryTxt: '#134E4A',
  accent:     '#F97316',
  bg:         '#F8FAFC',
  sidebar:    '#FFFFFF',
  border:     '#E2E8F0',
  text:       '#0F172A',
  muted:      '#64748B',
  dim:        '#94A3B8',
  white:      '#FFFFFF',
  success:    '#22C55E',
  error:      '#EF4444',
  errorBg:    '#FEE2E2',
  errorHov:   '#FECACA',
};

const FONT = "'Plus Jakarta Sans', system-ui, sans-serif";

// ─── Nav Item helpers (CC isolado por função) ──────────────────────────────────
function getNavItemStyle(ativo, hov) {
  return {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '9px 12px',
    borderRadius: 10,
    fontFamily: FONT,
    fontSize: 13.5,
    fontWeight: ativo ? 700 : 500,
    color: ativo ? C.primaryTxt : hov ? C.text : C.muted,
    background: ativo ? C.primaryBg : hov ? C.bg : 'transparent',
    border: 'none',
    borderLeft: ativo ? `3px solid ${C.primary}` : '3px solid transparent',
    cursor: 'pointer',
    outline: 'none',
    textAlign: 'left',
    transition: 'all 0.15s ease',
    position: 'relative',
  };
}

function getNavIconProps(ativo, hov) {
  return {
    size: 17,
    strokeWidth: ativo ? 2.5 : 2,
    color: ativo ? C.primary : hov ? C.text : C.dim,
    style: { flexShrink: 0, transition: 'color 0.15s' },
  };
}

function NavBadge({ badge, totalNaoLidas }) {
  if (!badge || totalNaoLidas <= 0) return null;
  return (
    <span style={{
      background: C.accent,
      color: '#fff',
      borderRadius: 20,
      padding: '0 6px',
      fontSize: 10,
      fontWeight: 800,
      minWidth: 18,
      height: 18,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {totalNaoLidas > 99 ? '99+' : totalNaoLidas}
    </span>
  );
}

// ─── Nav Item (sidebar) ────────────────────────────────────────────────────────
function NavItem({ id, label, Icon, badge, totalNaoLidas, ativo, onClick }) {
  const [hov, setHov] = useState(false);

  return (
    <button
      onClick={() => onClick(id)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={getNavItemStyle(ativo, hov)}
    >
      {Icon && <Icon {...getNavIconProps(ativo, hov)} />}
      <span style={{ flex: 1 }}>{label}</span>
      <NavBadge badge={badge} totalNaoLidas={totalNaoLidas} />
    </button>
  );
}

// ─── Sub Item (accordion) ─────────────────────────────────────────────────────
function SubItem({ id, label, Icon, ativo, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={() => onClick(id)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '7px 12px 7px 36px',
        borderRadius: 8,
        fontFamily: FONT,
        fontSize: 12.5,
        fontWeight: ativo ? 700 : 500,
        color: ativo ? C.primary : hov ? C.text : C.muted,
        background: ativo ? C.primaryMid + '55' : 'transparent',
        border: 'none',
        cursor: 'pointer',
        outline: 'none',
        textAlign: 'left',
        transition: 'all 0.12s ease',
      }}
    >
      {Icon && <Icon size={13} strokeWidth={2} color={ativo ? C.primary : C.dim} style={{ flexShrink: 0 }} />}
      {label}
    </button>
  );
}

// ─── Layout principal ─────────────────────────────────────────────────────────
export default function LayoutOrganizAI({
  abasVisiveis,
  abaAtiva,
  handleTrocarAba,
  subAbaDisparo,
  setSubAbaDisparo,
  subAbaFinanceiro,
  setSubAbaFinanceiro,
  expandidoDisparo,
  expandidoFinanceiro,
  SUB_ABAS_DISPARO,
  SUB_ABAS_FINANCEIRO,
  usuarioLogado,
  tenant,
  totalNaoLidas,
  atendenteAtivo,
  atendenteProcessando,
  handleToggleAtendente,
  handleLogout,
  children,
}) {
  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [logoutHov, setLogoutHov] = useState(false);

  // Carrega fonte
  useEffect(() => {
    const id = 'organizai-font';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap';
      document.head.appendChild(link);
    }
    // CSS responsivo e scroll
    const styleId = 'organizai-style';
    if (!document.getElementById(styleId)) {
      const s = document.createElement('style');
      s.id = styleId;
      s.textContent = `
        html { scroll-behavior: smooth; }
        * { box-sizing: border-box; }
        @media (max-width: 768px) {
          .org-sidebar-desktop { display: none !important; }
          .org-topbar-mobile { display: flex !important; }
        }
        @media (min-width: 769px) {
          .org-topbar-mobile { display: none !important; }
          .org-sidebar-desktop { display: flex !important; }
        }
        .org-nav-scroll::-webkit-scrollbar { width: 4px; }
        .org-nav-scroll::-webkit-scrollbar-track { background: transparent; }
        .org-nav-scroll::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }
      `;
      document.head.appendChild(s);
    }
  }, []);

  const mostrarAtendente = usuarioLogado?.perfil !== 'vendedor';

  // Conteúdo da sidebar (reutilizado no desktop e mobile)
  const SidebarContent = ({ onClose }) => {
    const estilosBotaoAtendente = () => ({
      width: '100%', display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 12px', borderRadius: 10,
      fontFamily: FONT, fontSize: 13, fontWeight: 600,
      background: atendenteAtivo ? '#D1FAE5' : C.bg,
      color: atendenteAtivo ? '#065F46' : C.muted,
      border: `1px solid ${atendenteAtivo ? '#6EE7B7' : C.border}`,
      cursor: atendenteProcessando ? 'not-allowed' : 'pointer',
      opacity: atendenteProcessando ? 0.6 : 1,
      outline: 'none', transition: 'all 0.15s',
    });

    const renderBotaoAtendente = () => {
      if (!mostrarAtendente || !handleToggleAtendente) return null;
      return (
        <button
          onClick={handleToggleAtendente}
          disabled={atendenteProcessando}
          style={estilosBotaoAtendente()}
        >
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: atendenteAtivo ? C.success : C.dim, flexShrink: 0, transition: 'background 0.2s' }} />
          <Power size={13} strokeWidth={2} />
          Atendente {atendenteAtivo ? 'ligado' : 'desligado'}
        </button>
      );
    };

    const getTemSubAbas = (id) => {
      if (id === 'disparo') return expandidoDisparo && SUB_ABAS_DISPARO?.length > 0;
      if (id === 'financeiro') return expandidoFinanceiro && SUB_ABAS_FINANCEIRO?.length > 0;
      return false;
    };

    const getSubAbaConfig = (id) => {
      if (id === 'disparo') return { subAbas: SUB_ABAS_DISPARO, subAbaAtiva: subAbaDisparo, setSubAba: setSubAbaDisparo };
      if (id === 'financeiro') return { subAbas: SUB_ABAS_FINANCEIRO, subAbaAtiva: subAbaFinanceiro, setSubAba: setSubAbaFinanceiro };
      return { subAbas: null, subAbaAtiva: null, setSubAba: null };
    };

    return (
    <div style={{
      width: 248,
      height: '100%',
      background: C.sidebar,
      display: 'flex',
      flexDirection: 'column',
      borderRight: `1px solid ${C.border}`,
      flexShrink: 0,
    }}>
      {/* Logo */}
      <div style={{ padding: '20px 16px 16px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <ListChecks size={18} color="#fff" strokeWidth={2} />
            </div>
            <div>
              <div style={{ fontFamily: FONT, fontWeight: 800, fontSize: 16, color: C.text, lineHeight: 1.1 }}>
                Organiza<span style={{ color: C.primary }}>+</span>
              </div>
              {tenant?.nome && (
                <div style={{ fontFamily: FONT, fontSize: 11, color: C.dim, marginTop: 2, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {tenant.nome}
                </div>
              )}
            </div>
          </div>
          {onClose && (
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.dim, padding: 4 }}>
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Navegação */}
      <nav className="org-nav-scroll" style={{ flex: 1, overflowY: 'auto', padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {(abasVisiveis || []).map(({ id, label, Icon, badge }) => {
          if (!podeAcessarAba(usuarioLogado, id)) return null;
          const ativo = abaAtiva === id;
          const temSubAbas = getTemSubAbas(id);
          const { subAbas, subAbaAtiva, setSubAba } = getSubAbaConfig(id);

          return (
            <div key={id}>
              <NavItem
                id={id} label={label} Icon={Icon} badge={badge}
                totalNaoLidas={totalNaoLidas} ativo={ativo}
                onClick={(id) => { handleTrocarAba(id); if (onClose) onClose(); }}
              />
              {ativo && temSubAbas && subAbas && (
                <div style={{ marginTop: 2, marginBottom: 4 }}>
                  {subAbas.map(({ id: sid, label: slabel, Icon: SIcon }) => (
                    <SubItem
                      key={sid} id={sid} label={slabel} Icon={SIcon}
                      ativo={subAbaAtiva === sid}
                      onClick={(sid) => { setSubAba(sid); if (onClose) onClose(); }}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Rodapé da sidebar */}
      <div style={{ padding: '12px 10px', borderTop: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>

        {/* Atendente */}
        {renderBotaoAtendente()}

        {/* Usuário */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 10, background: C.primaryBg, border: `1px solid ${C.primaryMid}` }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', background: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <User size={13} color="#fff" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700, color: C.primaryTxt, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {usuarioLogado?.nome || 'Usuário'}
            </div>
            <div style={{ fontFamily: FONT, fontSize: 10.5, color: C.primary }}>
              {usuarioLogado?.perfil || ''}
            </div>
          </div>
        </div>

        {/* Sair */}
        <button
          onClick={handleLogout}
          onMouseEnter={() => setLogoutHov(true)}
          onMouseLeave={() => setLogoutHov(false)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', borderRadius: 10,
            fontFamily: FONT, fontSize: 13, fontWeight: 600,
            background: logoutHov ? C.errorHov : C.errorBg,
            color: C.error,
            border: 'none', cursor: 'pointer', outline: 'none',
            transition: 'background 0.15s',
          }}
        >
          <LogOut size={14} strokeWidth={2} />
          Sair
        </button>
      </div>
    </div>
    );
  };

  return (
    <div style={{ display: 'flex', height: '100vh', background: C.bg, overflow: 'hidden', fontFamily: FONT }}>

      {/* ── SIDEBAR DESKTOP ── */}
      <div className="org-sidebar-desktop" style={{ height: '100%', flexShrink: 0 }}>
        <SidebarContent />
      </div>

      {/* ── TOPBAR MOBILE ── */}
      <div className="org-topbar-mobile" style={{
        display: 'none',
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        height: 56, background: C.white, borderBottom: `1px solid ${C.border}`,
        alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ListChecks size={15} color="#fff" strokeWidth={2} />
          </div>
          <span style={{ fontFamily: FONT, fontWeight: 800, fontSize: 15, color: C.text }}>
            Organiza<span style={{ color: C.primary }}>+</span>
          </span>
        </div>
        <button onClick={() => setSidebarAberta(true)} style={{ background: C.primaryBg, border: `1px solid ${C.primaryMid}`, borderRadius: 8, padding: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Menu size={20} color={C.primary} />
        </button>
      </div>

      {/* ── DRAWER MOBILE ── */}
      {sidebarAberta && (
        <>
          <div onClick={() => setSidebarAberta(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.35)', zIndex: 60, backdropFilter: 'blur(2px)' }} />
          <div style={{ position: 'fixed', top: 0, left: 0, height: '100%', zIndex: 70 }}>
            <SidebarContent onClose={() => setSidebarAberta(false)} />
          </div>
        </>
      )}

      {/* ── CONTEÚDO PRINCIPAL ── */}
      <main style={{
        flex: 1,
        position: 'relative',
        overflowY: 'auto',
        paddingTop: 0,
        // Em mobile, compensa a topbar fixa
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Espaçador mobile */}
        <div className="org-topbar-mobile" style={{ display: 'none', height: 56, flexShrink: 0 }} />
        {/* Saldo de disparos (some para quem não paga por disparo) */}
        <div style={{ position: 'absolute', top: 8, right: 16, zIndex: 30 }}>
          <SaldoDisparos tenantId={tenant?.id} />
        </div>
        {children}
      </main>
    </div>
  );
}
